import React from 'react'
import PropTypes from 'prop-types'
import clsx from 'clsx'
import { withI18next } from 'lib/i18n/withI18next'
import ResponsiveImage from 'components/images/responsive-image'
import { PRODUCT_LIST_LAYOUT_VIEW } from 'components/visibility/duck'
import ImageCarouselAutoPlayVideo from 'components/pdp/carousel-images/image-carousel/image-carousel-auto-play-video/ImageCarouselAutoPlayVideo'
import { segmentProductVideoViewedEvent } from 'components/product/product-segment/productSegment'
import getProductPremiumType from 'components/shared/product/utils/ProductPremiumUtils'
import ProductItemHoverInteractions from './hover-interactions'
import ProductPremiumTags from './product-premium-tags/ProductPremiumTags'
import BestSellersRankBadge from './best-sellers-rank-badge/BestSellersRankBadge'
import ProductItemSwipingGallery from './product-item-swiping-gallery/ProductItemSwipingGallery'
import FastSellingBadge from './fast-selling-badge/FastSellingBadge'

const ProductItemFigure = ({
  className,
  coverSrc,
  cy,
  displayHoverInteractions,
  displayPremiumTags,
  displayFlashSaleTitle,
  goToSimilarProducts,
  hoverSrc,
  isBackInStock,
  isBeautyProduct,
  isListView,
  isLowStock,
  isSoldOut,
  isComingSoon,
  isFlashSale,
  flash_sale_seconds_left,
  comingSoonText,
  name,
  onClick,
  options,
  productId,
  productUrl,
  refId,
  refType,
  stockQuantity,
  t,
  is_italist,
  view,
  isPhone,
  imageLoading,
  imageFetchPriority,
  full_video_url,
  full_video_cover_url,
  best_seller_rank,
  isShowBestSellerText,
  virtual_try_on_enabled,
  category_link_rewrite,
  id_category_default,
  images_array,
  badge,
}) => {
  const imgProps = {
    alt: name,
    bigDesktop: '18vw',
    desktop: '18vw',
    imgRatio: 1.33,
    laptop: '18vw',
    phone: '45vw',
    tablet: '18vw',
    // The explicit image-loading policy is limited to the Workday grid pilot.
    ...(imageLoading && { recoverOnError: true }),
  }

  const isShowBestSellerRank = Boolean(best_seller_rank)
  const isShowFastSellingBadge = badge?.type === 'selling_fast'

  const hasDisplayHoverInteractionsOrPremiumTags =
    displayHoverInteractions || displayPremiumTags

  const isShowVideo = isPhone
    ? Boolean(view === PRODUCT_LIST_LAYOUT_VIEW.BLOCK_VIEW && full_video_url)
    : Boolean(full_video_url)

  const isShowSwipingGallery =
    Boolean(view === PRODUCT_LIST_LAYOUT_VIEW.BLOCK_VIEW) &&
    isPhone &&
    images_array.length > 0

  const productBannerType = getProductPremiumType({
    isComingSoon,
    isSoldOut,
    isFlashSale,
    isLowStock,
    isBackInStock,
  })

  const eventData = {
    ref_id: refId,
    ref_type: refType,
    id_product: productId,
    site_category_name: category_link_rewrite,
    id_site_category: id_category_default,
  }

  const renderGallery = () => {
    if (isShowSwipingGallery)
      return (
        <div
          className={clsx({
            'with-premium-tags': !!productBannerType,
          })}
        >
          <ProductItemSwipingGallery
            images_array={images_array}
            eventData={eventData}
          />
        </div>
      )

    if (isShowVideo)
      return (
        <ImageCarouselAutoPlayVideo
          loop
          autoPlay
          poster={full_video_cover_url}
          src={full_video_url}
          className="product-image__video"
          customSegmentTracking={() => {
            segmentProductVideoViewedEvent({
              ...eventData,
            })
          }}
        />
      )

    if (coverSrc && hoverSrc)
      return (
        <React.Fragment>
          <div className="product-image__cover">
            <ResponsiveImage
              className={clsx({
                'product-image__italist-product': is_italist,
              })}
              {...imgProps}
              src={coverSrc}
              loading={imageLoading}
              fetchPriority={imageFetchPriority}
            />
          </div>
          <div className="product-image__hover">
            <ResponsiveImage
              className={clsx({
                'product-image__italist-product': is_italist,
              })}
              {...imgProps}
              src={hoverSrc}
              loading={imageLoading ? 'lazy' : undefined}
              fetchPriority={imageLoading ? 'low' : undefined}
            />
          </div>
        </React.Fragment>
      )

    if (coverSrc)
      return (
        <div className="product-image__cover">
          <ResponsiveImage
            className={clsx({
              'product-image__italist-product': is_italist,
            })}
            {...imgProps}
            src={coverSrc}
            loading={imageLoading}
            fetchPriority={imageFetchPriority}
          />
        </div>
      )

    if (hoverSrc)
      return (
        <div className="product-image__hover">
          <ResponsiveImage
            className={clsx({
              'product-image__italist-product': is_italist,
            })}
            {...imgProps}
            src={hoverSrc}
            loading={imageLoading}
            fetchPriority={imageFetchPriority}
          />
        </div>
      )

    return null
  }

  return (
    <a
      href={productUrl}
      className={clsx('product__link', { 'left-block': isListView })}
      onClick={onClick}
      data-cy={cy}
    >
      <figure className={clsx('product-image__fig', className || '')}>
        {isShowBestSellerRank && (
          <BestSellersRankBadge
            isShowBestSellerText={isShowBestSellerText}
            best_seller_rank={best_seller_rank}
          />
        )}
        {isShowFastSellingBadge && (
          <FastSellingBadge badgeText={badge?.value} />
        )}
        {renderGallery()}
        {!!hasDisplayHoverInteractionsOrPremiumTags && (
          <div className="product-image__fig__label">
            {displayHoverInteractions && (
              <ProductItemHoverInteractions
                goToSimilarProducts={goToSimilarProducts}
                options={options}
                productId={productId}
                refId={refId}
                refType={refType}
                showAddToBag={!isSoldOut}
                showSeeSimilar={!isBeautyProduct}
                productUrl={productUrl}
                virtual_try_on_enabled={virtual_try_on_enabled}
                category_link_rewrite={category_link_rewrite}
                id_category_default={id_category_default}
              />
            )}
            {displayPremiumTags && (
              <ProductPremiumTags
                t={t}
                cy={cy}
                productBannerType={productBannerType}
                comingSoonText={comingSoonText}
                isFlashSale={isFlashSale}
                flashSaleSecondsLeft={flash_sale_seconds_left}
                displayFlashSaleTitle={displayFlashSaleTitle}
                stockQuantity={stockQuantity}
              />
            )}
          </div>
        )}
      </figure>
    </a>
  )
}

ProductItemFigure.defaultProps = {
  className: '',
  coverSrc: '',
  cy: undefined,
  discountBadge: '',
  displayHoverInteractions: false,
  displayPremiumTags: true,
  displayFlashSaleTitle: true,
  hoverSrc: '',
  imageLoading: undefined,
  imageFetchPriority: undefined,
  best_seller_rank: null,
  isBackInStock: false,
  isBeautyProduct: false,
  isListView: false,
  isLowStock: false,
  isSoldOut: false,
  isComingSoon: false,
  isFlashSale: false,
  flash_sale_seconds_left: 0,
  comingSoonText: '',
  name: '',
  onClick: undefined,
  options: null,
  productUrl: '',
  refId: '',
  refType: null,
  stockQuantity: 0,
  is_italist: false,
  view: null,
  full_video_url: undefined,
  full_video_cover_url: undefined,
  isShowBestSellerText: true,
  virtual_try_on_enabled: false,
  id_category_default: null,
  category_link_rewrite: null,
  images_array: [],
  badge: null,
}

ProductItemFigure.propTypes = {
  className: PropTypes.string,
  coverSrc: PropTypes.string,
  cy: PropTypes.string,
  discountBadge: PropTypes.string,
  displayHoverInteractions: PropTypes.bool,
  displayPremiumTags: PropTypes.bool,
  displayFlashSaleTitle: PropTypes.bool,
  goToSimilarProducts: PropTypes.func.isRequired,
  hoverSrc: PropTypes.string,
  imageLoading: PropTypes.oneOf(['eager', 'lazy']),
  imageFetchPriority: PropTypes.oneOf(['high', 'low', 'auto']),
  isBackInStock: PropTypes.bool,
  best_seller_rank: PropTypes.number,
  isBeautyProduct: PropTypes.bool,
  isListView: PropTypes.bool,
  isLowStock: PropTypes.bool,
  isSoldOut: PropTypes.bool,
  isComingSoon: PropTypes.bool,
  isFlashSale: PropTypes.bool,
  flash_sale_seconds_left: PropTypes.number,
  comingSoonText: PropTypes.string,
  name: PropTypes.string,
  onClick: PropTypes.func,
  options: PropTypes.arrayOf(PropTypes.shape({})),
  productId: PropTypes.oneOfType([PropTypes.number, PropTypes.string])
    .isRequired,
  productUrl: PropTypes.string,
  stockQuantity: PropTypes.number,
  refId: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
  refType: PropTypes.string,
  t: PropTypes.func.isRequired,
  is_italist: PropTypes.bool,
  view: PropTypes.string,
  isPhone: PropTypes.bool.isRequired,
  full_video_url: PropTypes.string,
  full_video_cover_url: PropTypes.string,
  isShowBestSellerText: PropTypes.bool,
  virtual_try_on_enabled: PropTypes.bool,
  category_link_rewrite: PropTypes.string,
  id_category_default: PropTypes.oneOfType([
    PropTypes.number,
    PropTypes.string,
  ]),
  images_array: PropTypes.arrayOf(PropTypes.shape({})),
  badge: PropTypes.shape({
    type: PropTypes.string,
    value: PropTypes.string,
  }),
}

export { ProductItemFigure }

const Extended = withI18next()(ProductItemFigure)

export default Extended
